<template>
  <nav class="bg-white/90 shadow-md fixed w-full z-40 top-0">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-16">
        <!-- Logo (prend moins de place sur petit écran) -->
        <div class="flex items-center flex-shrink-0">
          <RouterLink to="/" class="flex items-center">
            <img
              class="h-8 sm:h-10 w-auto"
              src="@/assets/images/presentation/Logo_BdC.png"
              :alt="$t('navbar.brand')"
            />
            <span class="ml-2 text-lg sm:text-xl font-bold text-gray-800 hidden md:block">{{
              $t('navbar.brand')
            }}</span>
          </RouterLink>
        </div>

        <!-- Liens principaux (cachés sur mobile) -->
        <div class="hidden md:flex md:items-center md:space-x-2 lg:space-x-3">
          <!-- Utiliser une boucle et vérifier les rôles si nécessaire -->
          <RouterLink
            v-for="item in navigation"
            :key="item.name"
            :to="item.to"
            @click="handleLinkClick(item.to)"
            :class="[
              'px-3 py-2 rounded-md text-sm font-medium inline-flex items-center md:flex-col md:justify-center lg:flex-row',
              isActive(item.to)
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800',
            ]"
          >
            <component :is="item.icon" class="h-4 w-4 md:mb-1 lg:mb-0 lg:mr-1.5" />
            <span
              :class="['text-xs lg:text-sm', item.to === '/about' ? 'whitespace-nowrap' : '']"
              >{{ $t(item.name) }}</span
            >
          </RouterLink>
          <!-- Dashboard Dropdown pour Admin -->
          <div
            v-if="isAuthenticated && authStore.hasRole('admin')"
            ref="dashboardDropdownRef"
            class="relative"
          >
            <button
              @click="dashboardDropdownOpen = !dashboardDropdownOpen"
              class="px-3 py-2 rounded-md text-sm font-medium inline-flex items-center md:flex-col md:justify-center lg:flex-row text-gray-600 hover:bg-gray-50 hover:text-gray-800"
            >
              <Settings class="h-4 w-4 md:mb-1 lg:mb-0 lg:mr-1.5" />
              <div class="flex items-center">
                <span class="text-xs lg:text-sm">{{ $t('navbar.links.dashboard') }}</span>
                <ChevronDown class="h-3 w-3 ml-0.5 md:mt-0.5 lg:mt-0" />
              </div>
            </button>
            <transition name="fade">
              <div
                v-if="dashboardDropdownOpen"
                class="absolute right-0 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
              >
                <RouterLink
                  to="/researcher/dashboard"
                  @click="dashboardDropdownOpen = false"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {{ $t('navbar.links.researcher') }}
                </RouterLink>
                <RouterLink
                  to="/admin"
                  @click="dashboardDropdownOpen = false"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {{ $t('navbar.links.admin') }}
                </RouterLink>
              </div>
            </transition>
          </div>
          <!-- Dashboard Chercheur (pour les chercheurs non admin) -->
          <RouterLink
            v-else-if="isAuthenticated && authStore.hasRole('researcher')"
            to="/researcher/dashboard"
            @click="handleLinkClick('/researcher/dashboard')"
            :class="[
              'px-3 py-2 rounded-md text-sm font-medium inline-flex items-center md:flex-col md:justify-center lg:flex-row',
              isActive('/researcher')
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800',
            ]"
          >
            <Settings class="h-4 w-4 md:mb-1 lg:mb-0 lg:mr-1.5" />
            <span class="text-xs lg:text-sm">{{ $t('navbar.links.researcher') }}</span>
          </RouterLink>
        </div>

        <!-- Actions utilisateur & Menu Mobile -->
        <div class="flex items-center">
          <!-- Sélecteur de langue -->
          <div class="mr-3">
            <LanguageSwitcher />
          </div>

          <!-- Menu utilisateur (simplifié sur petit écran) -->
          <div
            v-if="isAuthenticated && currentUser"
            ref="userMenuRef"
            class="relative ml-3 hidden md:block"
          >
            <!-- Bouton Avatar (visible sur toutes tailles) -->
            <button
              @click.stop="userMenuOpen = !userMenuOpen"
              type="button"
              class="max-w-xs bg-white rounded-full flex items-center text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              id="user-menu-button"
              :aria-expanded="userMenuOpen"
              aria-haspopup="true"
            >
              <span class="sr-only">{{ $t('navbar.userMenu.open') }}</span>
              <BoringAvatar
                :size="40"
                :name="currentUser.firstName || currentUser.email"
                variant="beam"
                :colors="['#cb9836', '#a44525', '#212924', '#f7f2e0', '#cb9836']"
              />
            </button>
            <!-- Dropdown Desktop (Utiliser un VPopover ou équivalent pour mieux gérer) -->
            <transition name="fade">
              <div
                v-if="userMenuOpen"
                class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none hidden md:block"
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="user-menu-button"
                tabindex="-1"
              >
                <div class="px-4 py-2 text-sm text-gray-700 border-b">
                  {{ $t('navbar.userMenu.signedInAs') }} <br />
                  <span class="font-medium">{{
                    currentUser.firstName
                      ? `${currentUser.firstName} ${currentUser.lastName}`
                      : currentUser.email
                  }}</span>
                </div>
                <RouterLink
                  v-for="item in userNavigation"
                  :key="item.name"
                  :to="item.to"
                  @click="userMenuOpen = false"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  role="menuitem"
                  tabindex="-1"
                  >{{ $t(item.name) }}</RouterLink
                >
                <button
                  @click="handleLogout"
                  class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  role="menuitem"
                  tabindex="-1"
                >
                  {{ $t('navbar.userMenu.logout') }}
                </button>
              </div>
            </transition>
          </div>
          <!-- Bouton Connexion (si non connecté) -->
          <template v-else>
            <RouterLink
              to="/login"
              class="text-primary hover:text-primary-dark px-3 py-2 rounded-md text-sm font-medium flex items-center"
            >
              <LogIn class="h-5 w-5 sm:mr-1" />
              <span class="hidden sm:inline">{{ $t('navbar.userMenu.login') }}</span>
            </RouterLink>
          </template>

          <!-- Bouton Menu Mobile (Burger) -->
          <div ref="burgerButtonRef" class="ml-2 md:hidden">
            <button
              @click.stop="mobileMenuOpen = !mobileMenuOpen"
              type="button"
              class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
              aria-controls="mobile-menu"
              :aria-expanded="mobileMenuOpen"
            >
              <span class="sr-only">{{ $t('navbar.mobileMenu.open') }}</span>
              <Menu v-if="!mobileMenuOpen" class="block h-6 w-6" aria-hidden="true" />
              <X v-else class="block h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Panel Menu Mobile -->
    <transition name="slide-fade">
      <div
        v-if="mobileMenuOpen"
        ref="mobileMenuRef"
        class="md:hidden absolute top-16 inset-x-0 bg-white border-t border-gray-200 shadow-lg z-30"
        id="mobile-menu"
      >
        <div class="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          <RouterLink
            v-for="item in navigation"
            :key="item.name"
            :to="item.to"
            @click="handleLinkClick(item.to)"
            :class="[
              'px-3 py-2 rounded-md text-base font-medium inline-flex items-center w-full',
              isActive(item.to)
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800',
              item.to === '/about' ? 'whitespace-nowrap' : '',
            ]"
          >
            <component :is="item.icon" class="h-5 w-5 mr-2" />
            {{ $t(item.name) }}
          </RouterLink>
          <!-- Dashboard Dropdown Mobile pour Admin -->
          <div
            v-if="isAuthenticated && authStore.hasRole('admin')"
            ref="dashboardDropdownMobileRef"
            class="relative"
          >
            <button
              @click.stop="dashboardDropdownMobileOpen = !dashboardDropdownMobileOpen"
              class="px-3 py-2 rounded-md text-base font-medium inline-flex items-center w-full text-gray-600 hover:bg-gray-50 hover:text-gray-800"
            >
              <Settings class="h-5 w-5 mr-2" />
              {{ $t('navbar.links.dashboard') }}
              <svg class="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 20 20">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M7 7l3-3 3 3m0 6l-3 3-3-3"
                />
              </svg>
            </button>
            <transition name="fade">
              <div
                v-if="dashboardDropdownMobileOpen"
                class="mt-1 ml-4 w-36 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
              >
                <RouterLink
                  to="/researcher/dashboard"
                  @click="closeMobileDashboardDropdown"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {{ $t('navbar.links.researcher') }}
                </RouterLink>
                <RouterLink
                  to="/admin"
                  @click="closeMobileDashboardDropdown"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {{ $t('navbar.links.admin') }}
                </RouterLink>
              </div>
            </transition>
          </div>
          <!-- Dashboard Chercheur Mobile (pour les chercheurs non admin) -->
          <RouterLink
            v-else-if="isAuthenticated && authStore.hasRole('researcher')"
            to="/researcher/dashboard"
            @click="handleLinkClick('/researcher/dashboard')"
            :class="[
              'px-3 py-2 rounded-md text-base font-medium inline-flex items-center w-full',
              isActive('/researcher')
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800',
            ]"
          >
            <Settings class="h-5 w-5 mr-2" />
            {{ $t('navbar.links.researcher') }}
          </RouterLink>
        </div>
        <!-- Section utilisateur Mobile -->
        <div v-if="isAuthenticated && currentUser" class="pt-4 pb-3 border-t border-gray-200">
          <div class="flex items-center px-5">
            <div class="flex-shrink-0">
              <div class="h-10 w-10 rounded-full overflow-hidden">
                <BoringAvatar
                  :size="40"
                  :name="currentUser.firstName || currentUser.email"
                  variant="beam"
                  :colors="['#cb9836', '#a44525', '#212924', '#f7f2e0', '#cb9836']"
                />
              </div>
            </div>
            <div class="ml-3">
              <div class="font-medium text-base text-gray-800 dark:text-gray-200">
                {{ currentUser.firstName }} {{ currentUser.lastName }}
              </div>
              <div class="font-medium text-sm text-gray-500">{{ currentUser.email }}</div>
            </div>
            <!-- Bouton Déconnexion Mobile (déplacé ici) -->
            <button
              @click="handleLogout"
              type="button"
              class="ml-auto flex-shrink-0 bg-white p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              <span class="sr-only">{{ $t('navbar.userMenu.logout') }}</span>
              <LogOut class="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div class="mt-3 px-2 space-y-1">
            <RouterLink
              v-for="item in userNavigation"
              :key="item.name"
              :to="item.to"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              >{{ $t(item.name) }}</RouterLink
            >
            <!-- Suppression du bouton déconnexion du bas du menu -->
          </div>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script setup lang="ts">
import LanguageSwitcher from '@/components/UI/LanguageSwitcher.vue'
import { useAuthStore } from '@stores/auth'
import {
  ChevronDown,
  Eye,
  Home,
  Info,
  LogIn,
  LogOut,
  Menu,
  Newspaper,
  Search,
  Settings,
  X,
} from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import BoringAvatar from 'vue-boring-avatars'
import { RouterLink, useRoute, useRouter } from 'vue-router'

// Utiliser le store d'authentification
const authStore = useAuthStore()
// Utiliser storeToRefs pour garder la réactivité sur les propriétés du store
const { isAuthenticated, currentUser } = storeToRefs(authStore)
const { logout } = authStore // Action de déconnexion

const mobileMenuOpen = ref(false)
const userMenuOpen = ref(false) // Menu utilisateur desktop uniquement
const dashboardDropdownOpen = ref(false)
const dashboardDropdownMobileOpen = ref(false)
const route = useRoute()
const router = useRouter()
const userMenuRef = ref<HTMLElement | null>(null)
const burgerButtonRef = ref<HTMLElement | null>(null)
const mobileMenuRef = ref<HTMLElement | null>(null)
const dashboardDropdownRef = ref<HTMLElement | null>(null)
const dashboardDropdownMobileRef = ref<HTMLElement | null>(null)

const navigation = [
  { name: 'navbar.links.home', to: '/', icon: Home },
  { name: 'navbar.links.browse', to: '/browse', icon: Search },
  { name: 'navbar.links.viewer', to: '/viewer', icon: Eye },
  { name: 'navbar.links.news', to: '/news', icon: Newspaper },
  { name: 'navbar.links.about', to: '/about', icon: Info },
]

// Utiliser un computed pour l'état actif
const isActive = computed(() => (path: string) => {
  if (path === '/') {
    return route.path === '/'
  }
  return route.path.startsWith(path)
})

// Déconnexion
const handleLogout = async () => {
  console.log('[NavBar] Handling logout')
  mobileMenuOpen.value = false // Fermer le menu mobile si ouvert
  dashboardDropdownOpen.value = false
  dashboardDropdownMobileOpen.value = false
  logout()
  await router.push('/login')
}

// Menu Utilisateur
const userNavigation = [{ name: 'navbar.userMenu.profile', to: '/profile' }]

// Ajouter un gestionnaire de clic sur les liens
const handleLinkClick = (path: string) => {
  console.log('[NavBar] Link clicked:', path)
  mobileMenuOpen.value = false
  dashboardDropdownOpen.value = false
  dashboardDropdownMobileOpen.value = false
}

const closeMobileDashboardDropdown = () => {
  dashboardDropdownMobileOpen.value = false
  mobileMenuOpen.value = false
}

// Fermer le menu utilisateur desktop quand on clique en dehors
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node

  // Fermer le menu utilisateur desktop si on clique en dehors
  if (userMenuOpen.value && userMenuRef.value && !userMenuRef.value.contains(target)) {
    userMenuOpen.value = false
  }

  // Fermer le dropdown dashboard desktop si on clique en dehors
  if (
    dashboardDropdownOpen.value &&
    dashboardDropdownRef.value &&
    !dashboardDropdownRef.value.contains(target)
  ) {
    dashboardDropdownOpen.value = false
  }

  // Fermer le dropdown dashboard mobile si on clique en dehors
  if (
    dashboardDropdownMobileOpen.value &&
    dashboardDropdownMobileRef.value &&
    !dashboardDropdownMobileRef.value.contains(target)
  ) {
    dashboardDropdownMobileOpen.value = false
  }

  // Fermer le menu mobile si on clique en dehors (mais pas sur le bouton burger ou le menu lui-même)
  if (mobileMenuOpen.value) {
    const isClickOnBurgerButton = burgerButtonRef.value && burgerButtonRef.value.contains(target)
    const isClickOnMobileMenu = mobileMenuRef.value && mobileMenuRef.value.contains(target)

    if (!isClickOnBurgerButton && !isClickOnMobileMenu) {
      mobileMenuOpen.value = false
    }
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Styles Tailwind sont utilisés directement dans le template */

/* Ajustement pour que le contenu principal ne soit pas caché par la navbar fixe */
/* Ceci devrait être géré dans le layout ou la page principale */
/* .default-layout > main { padding-top: 4rem; } */

/* Transitions pour le menu mobile */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

/* Style pour la transition du dropdown desktop */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.1s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
